import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ProgressBar, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import postConsensusContractABI from '../../contracts/tgetherPostConsensus.json';
import communityConsensusContractABI from '../../contracts/tgetherCommunityConsensus.json';
import LinkTokenABI from '../../contracts/LinkToken.json';
import { formatEther } from 'viem';
import * as config from '../../config.tsx';

const postConsensusContractAddress = process.env.REACT_APP_POSTS_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;
const communityConsensusContractAddress = process.env.REACT_APP_COMMUNITY_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;
const linkTokenContract = process.env.REACT_APP_CHAINLINK_TOKEN_CONTRACT as `0x${string}`;

interface PostSubmissionModalProps {
    show: boolean;
    handleClose: () => void;
    postId: string;
    onSubmissionSuccess: () => void;
}

function PostSubmissionModal({ show, handleClose, postId, onSubmissionSuccess }: PostSubmissionModalProps) {
    const [communityName, setCommunityName] = useState('');
    const [error, setError] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [transactionAlert, setTransactionAlert] = useState('');
    const [consensusFee, setConsensusFee] = useState<string | null>(null); // Use string for formatted fee
    const [feeInWei, setFeeInWei] = useState<bigint>(BigInt(0)); // Use 'bigint' instead of 'BigInt'
    const [consensusTime, setConsensusTime] = useState<number | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 3;

    const navigate = useNavigate();

    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show]);

    const resetForm = () => {
        setCommunityName('');
        setConsensusTime(null);
        setConsensusFee(null);
        setCurrentStep(1);
        setError('');
        setInfoMessage('');
        setTransactionAlert('');
    };

    const approveLinkFee= async () => {

        const tx = await writeContract(config.config, {
          address: linkTokenContract,   // LINK token contract address
          abi: LinkTokenABI,            // Correct ABI for the LINK token
          functionName: 'approve',      // The function name (approve)
          args: [ process.env.REACT_APP_FUND_CONTRACT, BigInt("200000000000000000")],
          value: BigInt("0"),           // No value for this transaction
        });
    
        // Wait for the transaction to be mined
        const receipt = await waitForTransactionReceipt(config.config,{
          hash: tx,  // Transaction hash from the tx response
        });
        setConsensusFee(formatEther(BigInt("0")));
      }

    const handleFetchConsensusTimeAndFee = async () => {
        try {
            const communityConsensusTime = await readContract(config.config, {
                abi: communityConsensusContractABI.abi,
                address: communityConsensusContractAddress,
                functionName: 'getCommunityConsensousTime',
                args: [communityName],
            });

            const time = Number(communityConsensusTime);
            setConsensusTime(time);

            if (time === 0) {
                setInfoMessage('This community does not require consensus processing (Consensus Time is 0).');
                setConsensusFee('0');
            } else {
                // Fetch the consensus fee from the contract
                const consensusFeeData = await readContract(config.config, {
                    abi: postConsensusContractABI.abi,
                    address: postConsensusContractAddress,
                    functionName: 'consensusFee',
                });

                const _feeInWei = BigInt(consensusFeeData as string);
                const feeInEth = formatEther(_feeInWei);
                console.log("underscore:", _feeInWei)


                console.log('Fetched fee:', feeInEth); // Debugging log
                setFeeInWei(_feeInWei); // Store fee in Wei for transaction
                console.log(feeInWei);
                setConsensusFee(feeInEth); // Store formatted fee for display
                setInfoMessage('Consensus time and fee successfully fetched!');
            }

            setError('');
            handleNext(); // Proceed to the next step
        } catch (error) {
            console.error('Error fetching consensus time and fee:', error);
            setError('Failed to fetch consensus time and fee. Please try again.');
        }
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!postId || !communityName.trim()) {
                setError('Invalid post ID or community name.');
                return;
            }

            setTransactionAlert('Transaction is being processed. Please wait...');
            const sendValue = feeInWei
            console.log(sendValue)
            const tx = await writeContract(config.config, {
                abi: postConsensusContractABI.abi,
                address: postConsensusContractAddress,
                functionName: 'submitToCommunity',
                args: [postId, communityName],
                value: sendValue,
            });

            const receipt = await waitForTransactionReceipt(config.config, { hash: tx });

            setTransactionAlert('');
            onSubmissionSuccess();
            navigate(`/search-results?type=Post&value=${encodeURIComponent(postId)}`);
            handleClose();
        } catch (error) {
            console.error('Error submitting post to community:', error);

            if (error instanceof Error && error.message.includes('revert')) {
                setError('Transaction failed: Smart contract execution reverted. Please check your inputs.');
            } else if (error instanceof Error && error.message.includes('out of gas')) {
                setError('Transaction failed: Out of gas. Try increasing the gas limit.');
            } else {
                setError('Failed to submit post. Please try again.');
            }

            setTransactionAlert('');
        }
    };

    const renderTooltip = (text: string) => <Tooltip id="button-tooltip">{text}</Tooltip>;

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Form.Group>
                        <Form.Label>
                            Community Name{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip("Enter the name of the community to which you want to submit the post.")}
                            >
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter community name"
                            value={communityName}
                            onChange={(e) => setCommunityName(e.target.value)}
                        />
                    </Form.Group>
                );
            case 2:
                if (process.env.REACT_APP_CHAIN_ID === "0x66eee"){
                    return(
                      <div>
                      <p>Press the button to approve the testnet LINK transfer. You can go to 
                        <a href="https://faucets.chain.link/arbitrum-sepolia" target="_blank" rel="noopener noreferrer">
                          Arbitrum Sepolia Faucet
                        </a> to get funds.</p>
                      <Button style={{marginBottom:"10px"}}variant="outline-info" onClick={approveLinkFee}>
                        Approve 0.2 LINK for Fee
                      </Button>
                    </div>
          
                    )} else{
                return (
                    <>
                        <Button variant="secondary" onClick={handleFetchConsensusTimeAndFee}>
                            Fetch Consensus Time & Fee
                        </Button>
                        {infoMessage && <Alert variant="info" className="mt-3">{infoMessage}</Alert>}
                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                    </>
                );
                    }
            case 3:
                return (
                    <div style={{ marginTop: '10px' }}>
                        <p>Consensus Time: {consensusTime !== null ? `${consensusTime} seconds` : 'Not set'}</p>
                        <p>
                            Consensus Fee: {consensusFee !== null ? `${consensusFee} ETH` : 'Fetching...'}{' '}
                            <OverlayTrigger
                                placement="right"
                                overlay={renderTooltip(
                                    "This fee covers the gas cost required for processing the consensus. By signing this transaction, you acknowledge that this fee is part of a shared pool used for automated transactions and is not directly tied to your specific submission."
                                )}
                            >
                                <Button variant="secondary" className="ml-1 p-0" style={{ border: 'none', background: 'none' }}>
                                    <i className="bi bi-patch-question-fill"></i>
                                </Button>
                            </OverlayTrigger>
                        </p>
                        <p style={{ color: '#6c757d', fontSize: '.9rem' }}>
                            *Verify before signing the transaction that the value matches below. By signing this transaction, you understand this fee goes to part of the pool to fund automated transactions. Due to price volatility, we cannot guarantee automated upkeep.
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Submit Post to Community</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {transactionAlert && <Alert variant="info">{transactionAlert}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    {renderStep()}
                    <ProgressBar now={(currentStep / totalSteps) * 100} className="my-3" />
                    <div className="d-flex justify-content-between">
                        <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                            Previous
                        </Button>
                        {currentStep < totalSteps ? (
                            <Button variant="info" onClick={handleNext}>
                                Next
                            </Button>
                        ) : (
                            <Button variant="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        )}
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default PostSubmissionModal;
